import { jsx, jsxs } from "react/jsx-runtime";
import { string, func, bool, node, arrayOf, oneOfType, element } from "prop-types";
import React, { useEffect, useRef, useContext, useState } from "react";
import SSRIcon from "@ingka/ssr-icon";
import chevronDownSmall from "@ingka/ssr-icon/paths/chevron-down-small";
import Label from "@ingka/label";
import cx from "classnames";
import { FormFieldContext } from "@ingka/form-field";
const propTypes = {
  /** Unique ID of the select (required) */
  id: string.isRequired,
  /** Text that is passed to the Label component */
  label: oneOfType([string, element]),
  /** Text passed to the Label component */
  req: oneOfType([string, element]),
  /** Disabled state of the Select */
  disabled: bool,
  /** Nodes of type Option or OptGroup */
  children: oneOfType([
    arrayOf(node),
    node
  ]),
  /** Function invoked on changed selection. First argument is the event. Second argument is a boolean which is true if the hint text is selected. */
  onChange: func,
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** Can be used to add classes to base HTML element. */
  className: string,
  /** Sets aria-describedby on Select */
  describedById: string,
  /** Function returning react element with paths for SSRIcon from `@ingka/ssr-icon/paths/[icon].js` */
  ssrIcon: func,
  /** Sets hint option text for the Select element, null means don't show hint */
  hintText: string,
  /** Sets value of Select, set using state */
  value: string
};
const Select = (props) => {
  const {
    describedById,
    onChange,
    className,
    prefix = "",
    hintText = "Choose an option",
    id,
    label,
    ssrIcon,
    req,
    value,
    children,
    disabled = false,
    ...other
  } = props;
  const { shouldValidate, valid } = useContext(FormFieldContext);
  const [withHint, setWithHint] = useState(false);
  const selectRef = useRef();
  const hintOptionRef = useRef();
  const handleOnChange = (e) => {
    if (onChange) {
      onChange(e, Boolean(hintOptionRef.current && hintOptionRef.current.selected));
    }
  };
  useEffect(() => {
    if (hintOptionRef.current) {
      setWithHint(hintOptionRef.current.selected);
    }
  }, [value, children]);
  const baseClass = `${prefix}select`;
  const classes = cx(
    `${baseClass}`,
    shouldValidate && !valid && `${baseClass}--error`,
    shouldValidate && valid && `${baseClass}--success`,
    disabled && `${baseClass}--disabled`,
    ssrIcon && `${baseClass}--leading-icon`,
    `${prefix}label-wrapper ${prefix}label-wrapper--text-input`,
    withHint && `${baseClass}--hint`,
    className
  );
  return /* @__PURE__ */ jsxs("div", { className: classes, children: [
    label && /* @__PURE__ */ jsx(Label, { prefix, htmlFor: id, label, reqText: req }),
    /* @__PURE__ */ jsxs("div", { className: `${baseClass}__wrapper`, children: [
      /* @__PURE__ */ jsxs("select", { value, ref: selectRef, id, disabled, onChange: handleOnChange, "aria-describedby": describedById, ...other, children: [
        hintText && /* @__PURE__ */ jsx("option", { value: hintText, ref: hintOptionRef, children: hintText }),
        children
      ] }),
      ssrIcon && /* @__PURE__ */ jsx(SSRIcon, { className: `${baseClass}__icon`, paths: ssrIcon, prefix }),
      /* @__PURE__ */ jsx(SSRIcon, { className: `${baseClass}__chevron-down`, prefix, paths: chevronDownSmall }),
      /* @__PURE__ */ jsx("span", { className: `${baseClass}__border` })
    ] })
  ] });
};
Select.propTypes = propTypes;
var Select_default = Select;
export {
  Select_default as default
};
